import {Button} from "@wakelet/ui-kit";
import {FC, useState} from "react";
import {useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {FieldGroup, ValidationError} from "../form/ValidationError";
import {inputClass} from "../form/input-class";
import {translate, translateKey} from "../language/translate";
import {PasswordInput} from "../form/PasswordInput";
import {AccountsError, useLoginWithEmail} from "./useLoginWithEmail";
import {UserAccountStore} from "../auth/store";
import {Navigate, useSearchParams} from "react-router-dom";
import {startLogin} from "../../domain/login";
import {AuthProviderType} from "../../domain/providers";
import {Spinner} from "../loading/Spinner";
import {FieldLabel, RequiredFieldsMessage} from "../form/FieldLabel";
import {clearAuthIntent, trackLogIn} from "../../analytics/mixpanel";

export type EmailCredentials = {
  email: string
  password: string
  serverError: void
}

const schema = yup.object({
  email: yup.string().trim().email(translateKey("account:validation.email")).
    required(translateKey("account:validation.required.email")),
  password: yup.string().required(translateKey("account:validation.required.password")).
    min(6, translateKey("account:validation.password")),
}).required();

export type LoginWithEmailFormProps = {
  preloadEmail?: string
  readOnlyEmail?: boolean
  hideEmail?: boolean
}

export const LoginWithEmailForm: FC<LoginWithEmailFormProps> = ({preloadEmail, readOnlyEmail, hideEmail}) => {
  const {loginWithEmail, user} = useLoginWithEmail();
  const defaultValues = useDefaultFormValues();
  const [loading, setLoading] = useState(false);

  const {register, handleSubmit, formState: {errors}, setError, clearErrors} = useForm<EmailCredentials>({
    resolver: yupResolver(schema),
    defaultValues
  });

  if (user) {
    return <Navigate to="/authenticated"/>;
  }

  const doSubmit = (credentials: EmailCredentials) => {
    clearErrors();
    clearAuthIntent();
    setLoading(true);
    return loginWithEmail(credentials).then(() => {
      trackLogIn("email");
      startLogin(AuthProviderType.Password)
    }).catch((err: AccountsError) => {
      const {i18nKey} = err;
      setError("serverError", {message: i18nKey.key});
      setLoading(false);
    });
  }

  return (
    <form onSubmit={handleSubmit(doSubmit)} data-testid="email-login-form"
          aria-describedby={errors.serverError && "login-form-error"}
    >
      <FieldGroup className={hideEmail ? "hidden" : ""} dataTestId={"email-field-group"}>
        <FieldLabel htmlFor="email">{translateKey("account:placeholders.email")}</FieldLabel>
        <input id="email"
               value={preloadEmail}
               type={hideEmail ? "hidden" : "text"}
               readOnly={readOnlyEmail}
               {...register("email")}
               placeholder={translateKey("account:placeholders.email")}
               className={inputClass(errors.email)}
               autoComplete="email"
               aria-required={true}
               aria-hidden={hideEmail}
               aria-describedby={errors.email?.message && "email-error"}
               data-testid="email-field"/>
        <ValidationError message={errors.email?.message} id="email-error"/>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel htmlFor="password">{translateKey("account:placeholders.password")}</FieldLabel>
        <PasswordInput
          id="password"
          {...register("password")}
          placeholder={translateKey("account:placeholders.password")}
          className={inputClass(errors.password)}
          autoComplete="current-password"
          data-testid="password-field"
          aria-required={true}
          aria-describedby={errors.password?.message && "password-error"}
        />
        <ValidationError message={errors.password?.message} id="password-error"/>
        <RequiredFieldsMessage/>
      </FieldGroup>
      <FieldGroup>
        <ValidationError
          textCenter={true}
          widthRule={"max-w-xs"}
          message={errors.serverError?.message}
          id="login-form-error"/>
      </FieldGroup>
      <Button type="submit" variant={"primary"} className={"w-80"} data-testid="email-login-submit-btn"
              data-id="email-login" disabled={loading}>
        {loading && <div className="w-full flex justify-center"><Spinner/></div>}
        {!loading && <Button.Label text={translate("login.button", "account")}/>}
      </Button>
    </form>)
};

const useDefaultFormValues = () => {
  const [searchParams] = useSearchParams();
  const userAccounts = UserAccountStore.get();
  const accountIndex = searchParams.get("account");
  if (!accountIndex) {
    return {};
  }
  const index = parseInt(accountIndex);
  const account = userAccounts.list()[index];
  if (!account) {
    return {};
  }
  if (account.provider !== "password") {
    return {};
  }
  return {email: account.email}
};
